import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildStagedEntryOverlay,
  EntrySortingType,
  hasPriorityEntries,
  NotificationType,
  StagedEntryState,
  useTranslation,
} from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Alert, AlertVariant, Button, ButtonVariant, Tabs, TabStatusVariant, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import {
  Lorebook,
  LorebookEntry,
  LorebookEntryFixStatus,
  LorebookEntryOp,
  LorebookEntryOpData,
  LorebookEntryStatus,
  LorebookStatus,
  LorebookVisibility,
  ModerationStatus as ModerationStatusEnum,
  PostLorebookEntryRequest,
} from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldErrors, FieldValues, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { PageLayout, PageSize, PageVariant } from "@/components";
import DiscardChangesModalV2 from "@/components/common/DiscardChangesModalV2";
import ReactRouterPrompt from "@/components/common/ReactRouterPrompt";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useToast from "@/hooks/useToast";
import ModerationStatus from "@/pages/CreateCharacter/v2/components/ModerationStatus";
import SubmitForReviewModal from "@/pages/CreateCharacter/v2/components/SubmitForReviewModal";
import { findFirstInvalidRhfField, scrollToFirstInvalidField, stripLanguagePrefix } from "@/utils";

import { FORM_FIELDS, MAX_LOREBOOK_DESCRIPTION_LENGTH, MAX_LOREBOOK_NAME_LENGTH, MIN_PUBLIC_LOREBOOK_ENTRIES } from "../constants";
import { ILorebookDataFormProps } from "../types";
import isLorebookDetailsNeedAlert from "../utils/isLorebookDetailsNeedAlert";
import isLorebookEntriesNeedAlert from "../utils/isLorebookEntriesNeedAlert";
import { getLorebookCreateValidationSchema, getLorebookEditValidationSchema } from "../validationSchema";
import Entries from "./Entries";
import EntriesTabAlert from "./EntriesTabAlert";
import { FormField } from "./FormField";
import ModalEntry from "./ModalEntry";

// The Details and Entries tabs are sibling routes (/lorebook/edit/:id and /lorebook/edit/:id/entries)
// of one page, so a switch between them must not count as leaving it. Normalizing the trailing
// /entries segment lets the navigation guard compare them as the same location.
const stripEntriesSegment = (pathname: string) => pathname.replace(/\/entries\/?$/, "");

interface ILorebookFormProps {
  initialValues: ILorebookDataFormProps;
  onSubmit: (formData: FieldValues, submittingTab: TabNames) => void;
  isLoading: boolean;
  // A save failure (e.g. a moderation/underage rejection when publishing a Public lorebook),
  // rendered inline above the submitting tab's CTA instead of as a toast.
  submitError?: ILorebookSubmitError | null;
  onClearSubmitError?: () => void;
  initiallyDirty?: boolean;
  totalEntries?: number;
  status?: LorebookStatus;
  numRejectedEntries?: number;
  numNeedsReviewEntries?: number;
  onEntryMutated?: (entryCountDelta?: number) => void;
  isProcessingEntries?: boolean;
  lorebookData?: Lorebook;
  // Server-paginated, server-searched entries and controls (edit mode). Optional so create mode,
  // which has no entries tab, can omit them.
  entries?: LorebookEntry[];
  searchValue?: string;
  onSearchChange?: (next: string) => void;
  hasMore?: boolean;
  isLoadingFirstPage?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  sortType?: EntrySortingType;
  onSortChange?: (next: EntrySortingType) => void;
  // Staged-edit mode (public-lorebook moderation flow, flag-gated). When on, entry edits stage
  // locally and the list overlays the staged ops; the submit CTA sends the aggregated snapshot.
  isStagingMode?: boolean;
  hasStagedChanges?: boolean;
  stagedOpByEntryId?: Map<string, LorebookEntryOp>;
  onStageEntryCreate?: (entryData: PostLorebookEntryRequest) => void;
  onStageEntryUpdate?: (entryId: string, entryData: LorebookEntryOpData) => void;
  onStageEntryDelete?: (entryId: string) => void;
  // Drop the whole staging session (discarding unsaved changes).
  onDiscardStagedEdits?: () => void;
}

const EMPTY_ENTRIES: LorebookEntry[] = [];
const EMPTY_STAGED_OPS: Map<string, LorebookEntryOp> = new Map();

export enum TabNames {
  LOREBOOK = "lorebook",
  ENTRIES = "entries",
}

// A save failure paired with the tab whose CTA started that save. Tabs stay clickable while the
// request is in flight, so the active tab on failure is not necessarily the one that submitted;
// the error is only rendered on `tab`.
export interface ILorebookSubmitError {
  message: string;
  tab: TabNames;
}

const DETAILS_TAB_FIELD_ORDER = [
  FORM_FIELDS.NAME,
  FORM_FIELDS.DESCRIPTION,
  FORM_FIELDS.AVATAR,
  FORM_FIELDS.TAGS,
  FORM_FIELDS.VISIBILITY,
  FORM_FIELDS.GUIDELINES,
] as const;

export default function LorebookForm({
  isProcessingEntries,
  initialValues,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
  initiallyDirty = false,
  totalEntries: initialTotalEntries,
  status = LorebookStatus.DRAFT,
  numRejectedEntries = 0,
  numNeedsReviewEntries = 0,
  onEntryMutated,
  lorebookData,
  entries = EMPTY_ENTRIES,
  searchValue = "",
  onSearchChange,
  hasMore = false,
  isLoadingFirstPage = false,
  isLoadingMore = false,
  hasError = false,
  onLoadMore,
  onRetry,
  sortType = EntrySortingType.PRIORITY,
  onSortChange,
  isStagingMode = false,
  hasStagedChanges = false,
  stagedOpByEntryId = EMPTY_STAGED_OPS,
  onStageEntryCreate,
  onStageEntryUpdate,
  onStageEntryDelete,
  onDiscardStagedEdits,
}: ILorebookFormProps) {
  const { t } = useTranslation();
  const { hasFeatureFlag: hasLorebookImport } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_IMPORT);
  const isLorebookImportAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.LorebookImport);
  const applicationId = basicAppConfig.applicationId;
  const { displayToast } = useToast();
  const { id } = useParams();
  const isDetailsAlert = isLorebookDetailsNeedAlert(status);
  // The import banner reads its copy from these two counts, so it stays count-driven.
  const hasImportEntriesAlert = isLorebookEntriesNeedAlert(
    numRejectedEntries,
    numNeedsReviewEntries,
    lorebookData?.pending_ops_summary?.needs_fix ?? 0,
  );
  // num_flagged_entries covers moderator-flagged entries, which the import counts do not. It is
  // lorebook metadata, so it stays correct across every page of a server-paginated list.
  // pending_ops_summary.needs_fix covers admin-flagged staged ops on a public lorebook.
  const isEntriesAlert = hasImportEntriesAlert || (lorebookData?.num_flagged_entries ?? 0) > 0;
  // Requirement 3: with every entry approved and no moderation issues, there is nothing to
  // prioritise, so the Priority option is dropped from the sort menu. These counts come from the
  // lorebook metadata (the authoritative, unfiltered response), so they stay correct across all
  // pages — not just the ones currently loaded — and they do not drift while a sort is applied.
  const isPrioritySortAvailable = hasPriorityEntries(
    numRejectedEntries,
    numNeedsReviewEntries,
    lorebookData?.num_flagged_entries ?? 0,
    lorebookData?.pending_ops_summary?.needs_fix ?? 0,
  );
  // The backend total (num_entries) is authoritative for counts, the entry limit, and the
  // "public needs at least five entries" gate. The list itself is server-paginated via `entries`.
  const liveTotalEntriesCount = initialTotalEntries ?? 0;
  // In staging mode, overlay the staged ops onto the loaded page: staged creates prepend (badged
  // NEW), updates apply over the row (EDITED), deletes stay visible (REMOVED), and the count reflects
  // the proposed post-submit total. Off, this is a no-op pass-through of the live entries + count.
  const { rows: overlaidRows, effectiveTotal } = useMemo(
    () => buildStagedEntryOverlay(entries, isStagingMode ? stagedOpByEntryId : EMPTY_STAGED_OPS, liveTotalEntriesCount, searchValue),
    [entries, isStagingMode, stagedOpByEntryId, liveTotalEntriesCount, searchValue],
  );
  // An overlaid row IS a LorebookEntry (the staged marker is an extra field), so the list can consume
  // the rows directly; the badge map below carries the marker separately.
  const displayEntries = useMemo<LorebookEntry[]>(() => overlaidRows, [overlaidRows]);
  // entry id -> staged badge, so the list can badge a row after its own identity-based sort.
  const stagedStateByEntryId = useMemo(() => {
    const map = new Map<string, StagedEntryState>();
    for (const row of overlaidRows) {
      if (row.stagedState) {
        map.set(row.id, row.stagedState);
      }
    }
    return map;
  }, [overlaidRows]);
  // The staged-aware count/limit gate; equals the live total when nothing is staged.
  const totalEntriesCount = isStagingMode ? effectiveTotal : liveTotalEntriesCount;
  const [stateIsModalOpen, setStateIsModalOpen] = useState(false);
  const [stateEditIndex, setStateEditIndex] = useState<number | undefined>(undefined);
  const [stateIsUnsavedChanges, setStateIsUnsavedChanges] = useState(initiallyDirty);
  const [stateIsConfirmationModalVisible, setStateIsConfirmationModalVisible] = useState(false);
  const [statePendingFormData, setStatePendingFormData] = useState<FieldValues | null>(null);
  const refPendingModalSubmit = useRef(false);
  // True while a staging-mode moderation submit is in flight, so its success can reset the RHF form
  // (clearing isDirty). Without this, live parent fields are unchanged after a staged submit yet the
  // form stays dirty, keeping the CTA enabled and navigation blocked (a false "unsaved" state).
  const refPendingStagedSubmit = useRef(false);
  // Tab whose CTA opened the "Submit for Review" confirmation modal, so a resulting save error is
  // attributed to that tab rather than to whichever tab is active when the response lands.
  const refPendingSubmitTab = useRef<TabNames>(TabNames.LOREBOOK);
  const refIsInternalTabSwitch = useRef(false);
  const { pagePath, languageCode } = useLocalizedLocation();
  const [stateActiveTab, setStateActiveTab] = useState(() => {
    return pagePath.match(/\/entries\b/) ? TabNames.ENTRIES : TabNames.LOREBOOK;
  });
  const navigate = useNavigate();
  const validationSchema = useMemo(() => (id ? getLorebookEditValidationSchema(t) : getLorebookCreateValidationSchema(t)), [id, t, languageCode]);

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    watch,
    trigger,
    reset,
    getValues,
  } = useForm<ILorebookDataFormProps>({
    // zodResolver's types resolve against the zod v3 hoisted for the server, while this schema is
    // built with web-app's v4, so its overloads reject a valid schema. Runtime behaviour is unaffected.
    resolver: zodResolver(validationSchema as never),
    defaultValues: initialValues,
    mode: "onChange",
  });

  // Edit: surface validation for loaded lorebook data as soon as the form mounts (no user interaction required).
  useEffect(() => {
    if (!id) {
      return;
    }
    void trigger();
  }, [id, trigger, validationSchema]);

  const hasDetailsTabValidationErrors = useMemo(() => {
    const fieldErrors = errors as Record<string, unknown>;
    return DETAILS_TAB_FIELD_ORDER.some((key) => fieldErrors[key] != null);
  }, [errors]);

  const isDetailsTabNeedsReview = isDetailsAlert || hasDetailsTabValidationErrors;

  const lorebookTitle = initialValues[FORM_FIELDS.NAME];
  const pageTitle = useMemo(() => (id ? t("lorebook:title.edit") : t("lorebook:title.create")), [id, t]);
  const shouldShowLorebookTitle = useMemo(() => !!(id && lorebookTitle), [id, lorebookTitle]);
  const watchedVisibility = watch(FORM_FIELDS.VISIBILITY);

  // When a lorebook needs fixing ("Action Required"), its visibility is locked to the reviewed value.
  const isActionRequired =
    lorebookData?.moderationReportStatus === ModerationStatusEnum.FixRequired ||
    lorebookData?.moderationReport?.moderation_status === ModerationStatusEnum.FixRequired;

  const hasUnfixedFlaggedEntries = useMemo(
    () =>
      isActionRequired &&
      entries.some(
        (entry) =>
          (entry.status === LorebookEntryStatus.REJECTED ||
            entry.moderationReportStatus === ModerationStatusEnum.FixRequired ||
            entry.fix_status === LorebookEntryFixStatus.NEEDS_FIX) &&
          !stagedOpByEntryId.has(entry.id),
      ),
    [isActionRequired, entries, stagedOpByEntryId],
  );

  const submitButtonLabel = useMemo(() => {
    if (!id) {
      return t("lorebook:form.button.createContinue");
    }
    if (watchedVisibility === LorebookVisibility.PUBLIC || isActionRequired) {
      return t("lorebook:form.button.review");
    }
    return id ? t("lorebook:form.button.save") : t("lorebook:form.button.create");
  }, [id, t, watchedVisibility, isActionRequired]);

  // The Entries tab submit CTA exists for Public lorebooks only (edit mode), mirroring the Details tab CTA.
  const shouldShowEntriesSubmitCta = useMemo(
    () => !!id && (watchedVisibility === LorebookVisibility.PUBLIC || isActionRequired),
    [id, watchedVisibility, isActionRequired],
  );

  // A Public lorebook cannot be submitted with fewer than 5 entries, so surface the inline warning
  // proactively (not only after a submit attempt) whenever the count is below the minimum.
  const shouldShowNoEntriesWarning = useMemo(
    () => !!id && watchedVisibility === LorebookVisibility.PUBLIC && totalEntriesCount < MIN_PUBLIC_LOREBOOK_ENTRIES && !isProcessingEntries,
    [id, watchedVisibility, totalEntriesCount, isProcessingEntries],
  );

  // Staged entry edits are "unsaved" too, even when the RHF parent form is pristine — otherwise
  // leaving the page with pending, unsubmitted staged changes would skip the discard prompt.
  useEffect(() => {
    if (isDirty || (isStagingMode && hasStagedChanges)) {
      setStateIsUnsavedChanges(true);
    } else if (!initiallyDirty) {
      setStateIsUnsavedChanges(false);
    }
  }, [isDirty, initiallyDirty, isStagingMode, hasStagedChanges]);

  // Note: switching tabs deliberately does not clear the error. It is tagged with the tab that
  // submitted, so it simply stops rendering while another tab is active and reappears on return.
  // Clearing on switch would drop a moderation rejection the user never saw, since tabs stay
  // clickable while the save is in flight.

  // Dismiss the inline save error on any subsequent field edit. `isDirty` can't drive this: react-hook-form
  // keeps it `true` after the first change, so once the form is already dirty (the usual case when a save
  // fails) later edits wouldn't re-fire an isDirty-based effect. Subscribing to watch() catches every change.
  useEffect(() => {
    const subscription = watch(() => {
      if (submitError) {
        onClearSubmitError?.();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, submitError, onClearSubmitError]);

  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) => {
      // A programmatic tab switch within the form (e.g. redirecting to Details to surface
      // validation errors) is not the user leaving the page, so do not block it.
      if (refIsInternalTabSwitch.current) return false;
      if (!stateIsUnsavedChanges) return false;
      const currentPath = stripLanguagePrefix(currentLocation.pathname);
      const nextPath = stripLanguagePrefix(nextLocation.pathname);
      if (currentPath === nextPath) return false;
      // Switching between the Details and Entries tabs stays on this page, so it is not a discard.
      if (stripEntriesSegment(currentPath) === stripEntriesSegment(nextPath)) return false;
      return true;
    },
    [stateIsUnsavedChanges],
  );

  useEffect(() => {
    if (pagePath.match(/\/entries\b/)) {
      setStateActiveTab(TabNames.ENTRIES);
    } else {
      setStateActiveTab(TabNames.LOREBOOK);
    }
  }, [pagePath]);

  // Legacy (non-staging) mode: ModalEntry performs the create/update/delete against the API directly;
  // reloading the server-paginated list (via onEntryMutated) is the single source of truth, so there
  // is no local array to mutate here. The delta only reconciles the count optimistically: creating
  // adds one, editing an existing entry (numeric index) leaves it unchanged, deleting removes one.
  //
  // In staging mode nothing hit the server, so there is nothing to reload — the staged-op overlay
  // already reflects the change and the effective count. Skip onEntryMutated entirely.
  const handleAddEntry = useCallback(
    (entryData: unknown, index?: number) => {
      if (isStagingMode) {
        // A staged create is hidden by the overlay when it stops matching the active search — on the
        // initial create or a later edit — so clear the search to keep the just-touched row visible.
        if (searchValue) {
          onSearchChange?.("");
        }
        return;
      }
      onEntryMutated?.(typeof index === "number" ? 0 : 1);
    },
    [isStagingMode, onEntryMutated, searchValue, onSearchChange],
  );

  const handleDeleteEntry = useCallback(() => {
    if (isStagingMode) return;
    onEntryMutated?.(-1);
  }, [isStagingMode, onEntryMutated]);

  const handleOpenModal = useCallback((index?: number) => {
    setStateEditIndex(index);
    setStateIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setStateIsModalOpen(false);
    setStateEditIndex(undefined);
  }, []);

  const submitFormData = useCallback(
    (formData: FieldValues, submittingTab: TabNames) => {
      setStateIsUnsavedChanges(false);
      // Mark a staged submit so its success can clear the RHF dirty state (see the effect below).
      refPendingStagedSubmit.current = isStagingMode;
      onSubmit?.(formData, submittingTab);
    },
    [onSubmit, isStagingMode],
  );

  const handleOnSubmit = useCallback(
    (formData: FieldValues, submittingTab: TabNames) => {
      // The "Submit for Review" confirmation is an edit-flow step. On create the lorebook is created
      // as Private (review happens later in the Edit/Entries flow), so create submits directly.
      if (id && (formData[FORM_FIELDS.VISIBILITY] === LorebookVisibility.PUBLIC || isActionRequired)) {
        setStatePendingFormData(formData);
        // The confirmation modal defers the submit, so remember which tab's CTA opened it.
        refPendingSubmitTab.current = submittingTab;
        setStateIsConfirmationModalVisible(true);
        return;
      }
      submitFormData(formData, submittingTab);
    },
    [id, isActionRequired, submitFormData],
  );

  const onModalClose = useCallback(() => {
    setStateIsConfirmationModalVisible(false);
    setStatePendingFormData(null);
    refPendingModalSubmit.current = false;
  }, []);

  const onModalConfirm = useCallback(async () => {
    if (!statePendingFormData) {
      return;
    }
    refPendingModalSubmit.current = true;
    submitFormData(statePendingFormData, refPendingSubmitTab.current);
  }, [statePendingFormData, submitFormData]);

  useEffect(() => {
    if (!refPendingModalSubmit.current || isLoading) {
      return;
    }
    refPendingModalSubmit.current = false;
    onModalClose();
  }, [isLoading, onModalClose]);

  // After a staged moderation submit resolves, clear the RHF dirty state on success: the edits are now
  // pending review, so the form should read as saved (no navigation block, CTA disabled). A failed
  // submit surfaces submitError and keeps the form dirty so the user can retry.
  useEffect(() => {
    if (!refPendingStagedSubmit.current || isLoading) {
      return;
    }
    refPendingStagedSubmit.current = false;
    if (submitError) {
      // The optimistic clear in submitFormData must be undone: staged ops survive a failed submit, so
      // navigation has to stay blocked. No state change re-fires the unsaved-changes effect here.
      setStateIsUnsavedChanges(true);
      return;
    }
    // Reset to the just-submitted values so isDirty resets without discarding what the user sees.
    reset(getValues());
  }, [isLoading, submitError, reset, getValues]);

  const handleInvalidSubmit = useCallback((validationErrors: FieldErrors) => {
    scrollToFirstInvalidField(findFirstInvalidRhfField({ errors: validationErrors, fieldOrder: [...DETAILS_TAB_FIELD_ORDER] }));
  }, []);

  const tabItems = useMemo(
    () => [
      {
        name: TabNames.LOREBOOK,
        label: t("lorebook:form.tab.details"),
        url: ROUTES.lorebookEdit.replace(":id", id as string),
        status: isDetailsTabNeedsReview ? TabStatusVariant.NEEDS_REVIEW : undefined,
      },
      {
        name: TabNames.ENTRIES,
        label: t("lorebook:form.tab.entries"),
        url: ROUTES.lorebookEditEntries.replace(":id", id as string),
        status: isEntriesAlert ? TabStatusVariant.NEEDS_REVIEW : undefined,
      },
    ],
    [id, t, isDetailsTabNeedsReview, isEntriesAlert],
  );

  // Intercept tab change if there are unsaved changes
  const onTabChange = useCallback(
    (tabName: string) => {
      if (tabName === stateActiveTab) return; // Do nothing if re-clicking same tab
      // Details and Entries are two tabs of one page, so switching between them is never a discard:
      // edits on either tab are kept and are only submitted (or dropped) when the user saves or
      // leaves the page entirely (ReactRouterPrompt below). Prompting here would also wipe staged
      // entry ops made on the other tab in staging mode.
      setStateActiveTab(tabName as TabNames);
      navigate(tabItems.find((tab: { name: string; label: string; url: string }) => tab.name === tabName)?.url as string);
    },
    [stateActiveTab, tabItems, navigate],
  );

  // In staging mode, staged entry ops are "changes" even when the RHF parent form is pristine, so the
  // submit CTA must enable on staged changes too — otherwise a user who only added/edited/removed
  // entries could never submit them for review.
  const isSaveButtonVisuallyDisabled = isActionRequired
    ? hasUnfixedFlaggedEntries
    : !isDirty && !initiallyDirty && !(isStagingMode && hasStagedChanges);

  const navigateToDetailsTab = useCallback(() => {
    // Flag the switch as internal so the unsaved-changes guard does not block it with a discard prompt.
    refIsInternalTabSwitch.current = true;
    setStateActiveTab(TabNames.LOREBOOK);
    navigate(tabItems.find((tab: { name: string; label: string; url: string }) => tab.name === TabNames.LOREBOOK)?.url as string);
    refIsInternalTabSwitch.current = false;
  }, [navigate, tabItems]);

  const navigateToEntriesTab = useCallback(() => {
    // Flag the switch as internal so the unsaved-changes guard does not block it with a discard prompt.
    refIsInternalTabSwitch.current = true;
    setStateActiveTab(TabNames.ENTRIES);
    navigate(tabItems.find((tab: { name: string; label: string; url: string }) => tab.name === TabNames.ENTRIES)?.url as string);
    refIsInternalTabSwitch.current = false;
  }, [navigate, tabItems]);

  // Details tab submit. The "at least five entries" rule only applies in edit mode: at create time
  // the lorebook does not exist yet and entries are added afterwards, so create must always submit
  // through (visibility is forced to Private on create). In edit mode, a Public lorebook below the
  // minimum navigates to the Entries tab and surfaces the inline error there.
  const handleDetailsTabValidSubmit = useCallback(
    (formData: FieldValues) => {
      // The min-entries warning is shown proactively on the Entries tab; just route the user there.
      if (id && formData[FORM_FIELDS.VISIBILITY] === LorebookVisibility.PUBLIC && totalEntriesCount < MIN_PUBLIC_LOREBOOK_ENTRIES) {
        navigateToEntriesTab();
        return;
      }
      handleOnSubmit(formData, TabNames.LOREBOOK);
    },
    [id, totalEntriesCount, navigateToEntriesTab, handleOnSubmit],
  );

  const onFormSubmit = useCallback(() => {
    if (id && isSaveButtonVisuallyDisabled) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    void handleSubmit(handleDetailsTabValidSubmit, handleInvalidSubmit)();
  }, [displayToast, handleDetailsTabValidSubmit, handleInvalidSubmit, handleSubmit, id, isSaveButtonVisuallyDisabled, t]);

  // Entries tab submit (Public only): mirrors the Details tab submission flow.
  // If details have validation errors, navigate to the Details tab to surface them;
  // if there are no entries, show an inline error and stay on the Entries tab.
  const handleEntriesTabInvalidSubmit = useCallback(() => {
    navigateToDetailsTab();
  }, [navigateToDetailsTab]);

  const handleEntriesTabValidSubmit = useCallback(
    (formData: FieldValues) => {
      // A Public publish needs at least five entries; the inline warning is already shown proactively.
      // An Action Required resubmit is not a publish, so it must not be swallowed here.
      if (totalEntriesCount < MIN_PUBLIC_LOREBOOK_ENTRIES && shouldShowNoEntriesWarning) {
        return;
      }
      // Staging mode only applies to already-public lorebooks, so "Submit for Review" is not a
      // publish — with a pristine form and no staged ops it would PATCH a no-op moderation snapshot
      // the backend rejects. Apply the same no-changes guard the Details tab uses.
      if ((isStagingMode || isActionRequired) && isSaveButtonVisuallyDisabled) {
        displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
        return;
      }
      handleOnSubmit(formData, TabNames.ENTRIES);
    },
    [totalEntriesCount, shouldShowNoEntriesWarning, isStagingMode, isActionRequired, isSaveButtonVisuallyDisabled, displayToast, t, handleOnSubmit],
  );

  const onEntriesTabSubmit = useCallback(() => {
    // "Submit for Review" is a publish action, not a details save, so the no-changes guard does not
    // apply: the user may publish a populated draft they did not edit this session, and an entry-less
    // lorebook must still run validation to surface its inline warning.
    void handleSubmit(handleEntriesTabValidSubmit, handleEntriesTabInvalidSubmit)();
  }, [handleEntriesTabValidSubmit, handleEntriesTabInvalidSubmit, handleSubmit]);

  // Index into the displayed list. In staging mode that is the overlaid list (staged creates
  // prepended, updates applied), so opening a staged row edits the right entry; off, it is the live
  // entries list. Entries maps its post-sort rows back to this same array via originalIndex.
  const editEntryData = useMemo(() => {
    if (typeof stateEditIndex !== "number") return undefined;
    return displayEntries?.[stateEditIndex];
  }, [stateEditIndex, displayEntries]);

  // A lorebook under moderation review must not be edited, so entry mutations (e.g. Add Entry) are blocked.
  const isUnderReview =
    lorebookData?.moderationReportStatus === ModerationStatusEnum.Pending ||
    lorebookData?.moderationReportStatus === ModerationStatusEnum.RereviewRequired;
  const isFormDisabled = useMemo(() => {
    return isLoading || isUnderReview;
  }, [isLoading, isUnderReview]);
  const isEntriesSubmitVisuallyDisabled = isActionRequired || isStagingMode ? isSaveButtonVisuallyDisabled : false;

  return (
    <>
      {/* Intercept browser navigation */}
      <ReactRouterPrompt when={shouldBlockNavigation}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive ? (
            <DiscardChangesModalV2
              heading={t("lorebook:modal.unsavedChanges.title")}
              description={t("lorebook:modal.unsavedChanges.message")}
              isVisible={true}
              onUserConfirmation={() => {
                // Leaving the page discards unsaved work, so drop the staged session with it.
                onDiscardStagedEdits?.();
                onConfirm();
              }}
              handleClose={onCancel}
            />
          ) : null
        }
      </ReactRouterPrompt>
      <PageLayout size={PageSize.Medium} variant={PageVariant.Modal}>
        <PageLayout.Header
          title={pageTitle}
          actionTo="https://docs.spicychat.ai/product-guides/lorebook"
          actionLabel={t("lorebook:form.guide.link")}
          tooltip={t("lorebook:form.tooltip.description")}
        />
        {shouldShowLorebookTitle ? (
          <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className="text-gray-11 mt-sm">
            {lorebookTitle}
          </Text>
        ) : null}
        <PageLayout.Content classNames="flex flex-col gap-xl pb-0 pt-lg">
          {id ? <Tabs tabItems={tabItems} activeTab={stateActiveTab} onTabChange={onTabChange} /> : null}
          <ModerationStatus
            applicationId={applicationId as string}
            moderationReasonCodes={lorebookData?.moderationReport?.moderation_reason_codes}
            actionTakenDescription={lorebookData?.moderationReport?.action_taken_description}
            reasonForModeration={lorebookData?.moderationReport?.reason_for_moderation}
            moderationStatus={lorebookData?.moderationReport?.moderation_status as ModerationStatusEnum}
            reportsType={Array.from(lorebookData?.reportsType ?? [])}
            category="lorebook"
          />
          {stateActiveTab === TabNames.ENTRIES && (
            <>
              {hasLorebookImport && isLorebookImportAllowed && hasImportEntriesAlert && !isProcessingEntries ? (
                <EntriesTabAlert
                  data-testid="EntriesTabAlert"
                  numRejectedEntries={numRejectedEntries}
                  numNeedsReviewEntries={numNeedsReviewEntries}
                />
              ) : null}
              <Entries
                hideWarning={isUnderReview}
                isUnderReview={isUnderReview}
                entries={displayEntries}
                stagedStateByEntryId={stagedStateByEntryId}
                onOpenModal={handleOpenModal}
                totalEntries={totalEntriesCount}
                isProcessingEntries={isProcessingEntries}
                isFormDisabled={isFormDisabled}
                showNoEntriesError={shouldShowNoEntriesWarning}
                searchValue={searchValue}
                onSearchChange={onSearchChange}
                hasMore={hasMore}
                isLoadingFirstPage={isLoadingFirstPage}
                isLoadingMore={isLoadingMore}
                hasError={hasError}
                onLoadMore={onLoadMore}
                onRetry={onRetry}
                sortType={sortType}
                onSortChange={onSortChange}
                isPrioritySortAvailable={isPrioritySortAvailable}
              />
              {shouldShowEntriesSubmitCta ? (
                <div className="flex flex-col gap-2">
                  {submitError?.tab === TabNames.ENTRIES ? (
                    <Text
                      role="alert"
                      aria-live="polite"
                      data-testid="LorebookForm-EntriesTabSubmitError"
                      className="text-left text-red-9"
                      variant={TextVariants.LABEL_SMALL_REGULAR}
                    >
                      {submitError.message}
                    </Text>
                  ) : null}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant={ButtonVariant.CONTAINED}
                      type="button"
                      onClick={onEntriesTabSubmit}
                      loading={isLoading}
                      aria-disabled={isEntriesSubmitVisuallyDisabled || isFormDisabled}
                      className={isEntriesSubmitVisuallyDisabled || isFormDisabled ? "opacity-50 cursor-not-allowed" : ""}
                      disabled={isFormDisabled || hasUnfixedFlaggedEntries}
                      data-testid="LorebookForm-EntriesTabSubmitButton"
                    >
                      {submitButtonLabel}
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
          {stateActiveTab === TabNames.LOREBOOK && (
            <form onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-5">
              {hasLorebookImport && isLorebookImportAllowed && isDetailsAlert ? (
                <Alert
                  data-testid="DetailsTabModerationAlert"
                  variant={AlertVariant.ALERT}
                  title={t("lorebook:form.detailsTabModerationAlert.title")}
                  description={t("lorebook:form.detailsTabModerationAlert.description")}
                  visible={true}
                  isDismissable={false}
                />
              ) : null}

              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                <Trans i18nKey="lorebook:form.subheader" />
              </Text>
              <FormField
                label={t("lorebook:form.field.name.label")}
                type="text"
                name={FORM_FIELDS.NAME}
                control={control}
                errors={errors}
                required
                placeholder={t("lorebook:form.field.name.placeholder")}
                helperText={t("lorebook:form.field.name.helper")}
                maxLength={MAX_LOREBOOK_NAME_LENGTH}
                disabled={isFormDisabled}
              />
              <FormField
                type="textarea"
                name={FORM_FIELDS.DESCRIPTION}
                control={control}
                errors={errors}
                required
                label={t("lorebook:form.field.description.label")}
                placeholder={t("lorebook:form.field.description.placeholder")}
                helperText={t("lorebook:form.field.description.helper")}
                maxLength={MAX_LOREBOOK_DESCRIPTION_LENGTH}
                disabled={isFormDisabled}
              />
              <FormField
                type="avatar"
                name={FORM_FIELDS.AVATAR}
                control={control}
                errors={errors}
                required
                label={t("lorebook:form.field.coverImage.label")}
                helperText={t("lorebook:form.field.coverImage.helper")}
                disabled={isFormDisabled}
              />
              <FormField
                type="tags"
                name={FORM_FIELDS.TAGS}
                control={control}
                errors={errors}
                required={watch(FORM_FIELDS.VISIBILITY) === LorebookVisibility.PUBLIC}
                label={t("lorebook:form.field.tags.label")}
                placeholder={t("lorebook:form.field.tags.placeholder")}
                helperText={t("lorebook:form.field.tags.helper")}
                disabled={isFormDisabled}
              />
              <FormField
                type="visibility"
                lorebook={lorebookData}
                name={FORM_FIELDS.VISIBILITY}
                control={control}
                errors={errors}
                label={t("lorebook:form.field.visibility.label")}
                helperText={t("lorebook:form.field.visibility.helper")}
                disabled={isFormDisabled || isActionRequired || watch(FORM_FIELDS.VISIBILITY) === LorebookVisibility.PERMANENTLY_PRIVATE}
              />
              <div className="flex flex-col gap-5">
                {!id && (
                  <>
                    <FormField
                      type="checkbox"
                      name={FORM_FIELDS.GUIDELINES}
                      control={control}
                      errors={errors}
                      required={!id}
                      disabled={isLoading}
                      label={
                        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
                          <Trans i18nKey="lorebook:form.field.agree.text" />{" "}
                          <LocalizedLink
                            to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.guidelines}
                            target="_blank"
                            className="text-blue-10"
                            aria-label="community-guidelines"
                          >
                            <Trans i18nKey="lorebook:form.field.agree.link" />
                          </LocalizedLink>
                        </Text>
                      }
                    />
                    <Text className="text-right text-gray-11" variant={TextVariants.LABEL_SMALL_REGULAR}>
                      <Trans i18nKey="lorebook:form.helper.entriesAfterCreate" />
                    </Text>
                  </>
                )}
                {submitError?.tab === TabNames.LOREBOOK ? (
                  <Text
                    role="alert"
                    aria-live="polite"
                    data-testid="LorebookForm-DetailsTabSubmitError"
                    className="text-left text-red-9"
                    variant={TextVariants.LABEL_SMALL_REGULAR}
                  >
                    {submitError.message}
                  </Text>
                ) : null}
                <div className="flex justify-end gap-2">
                  <Button
                    variant={ButtonVariant.CONTAINED}
                    type="button"
                    onClick={onFormSubmit}
                    loading={isLoading}
                    aria-disabled={isSaveButtonVisuallyDisabled || isFormDisabled}
                    className={isSaveButtonVisuallyDisabled || isFormDisabled ? "opacity-50 cursor-not-allowed" : ""}
                    disabled={isFormDisabled || hasUnfixedFlaggedEntries}
                    data-testid="LorebookForm-DetailsTabSubmitButton"
                  >
                    {submitButtonLabel}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </PageLayout.Content>
      </PageLayout>
      <ModalEntry
        isOpen={stateIsModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddEntry}
        onDelete={handleDeleteEntry}
        editIndex={stateEditIndex}
        entriesCount={totalEntriesCount}
        lorebookId={id}
        entryData={editEntryData}
        isFormDisabled={isFormDisabled}
        isStagingMode={isStagingMode}
        onStageEntryCreate={onStageEntryCreate}
        onStageEntryUpdate={onStageEntryUpdate}
        onStageEntryDelete={onStageEntryDelete}
      />
      <SubmitForReviewModal
        title={t("lorebook:modal.review.heading")}
        onUserConfirmation={onModalConfirm}
        confirmationMessage={t("lorebook:modal.review.confirmationMessage")}
        isVisible={stateIsConfirmationModalVisible}
        actionButtonText={t("lorebook:modal.review.actionButton")}
        discardButtonText={t("lorebook:modal.review.discardButton")}
        isLoading={isLoading}
        handleClose={onModalClose}
      />
    </>
  );
}
